import _ from 'lodash'
import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import * as graph from '@adc/sdk'
import { match, P } from 'ts-pattern'
import {
  Text,
  PopupTooltip,
  Icon,
  Button,
  utils,
  Pulser,
  constants,
  useApplicationContext,
} from '@adc/ui-adc'
import * as blueprint from '@adc/blueprint'
import { isFCLMarketing } from 'common/modules/dueDiligence'
import { Property, LiveAuction } from '@adc/sdk/types'
import { CombinedListingType, useListingContext } from 'common/context/listing'
import {
  PropertyPageActionsType,
  usePropertyContext,
} from 'common/context/property'
import { SAVE_PROPERTY_CTA_TPS, DAY_OF_MONTH_FORMAT } from '../../../statics'
import { date } from '../../../services/tps_formatter'
import { PresenceContext } from '../../../contexts'
import BidAtCountySite from '../bidAtCountySite'
import { getAiFaqMode } from '../../faqsWithAi'
import AuctionDetailsTemplate, {
  SavePropertyButton,
  RemoteBidButton,
  whySaveLink,
  tpsAdditionalContent,
  similarPropertiesLink,
  remoteBidEducationLink,
  RowDataProps,
} from '../template'
import styles from '../index.scss'
import hasSimilarProperties from '../../../utils/hasSimilarProperties'
import { formattedVenueAddress, isAuctionSold } from '../utils'
import CalendarPopup from './calendar'
import { getERVRow } from './common'

const cx = classnames.bind(styles)

const TruncateText = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <PopupTooltip
      position='bottom center'
      trigger={<span className={cx('truncate', className)}>{children}</span>}
      content={<span>{children}</span>}
      offset={undefined}
      horizontalOffset={undefined}
      verticalOffset={undefined}
    />
  )
}

TruncateText.propTypes = {
  children: PropTypes.string,
  className: PropTypes.string,
}

interface DetailContentProps {
  isInTour?: boolean
  isRemoteBidEnabled: boolean
  listing: CombinedListingType
  isRedesignEnabled?: boolean
  isAuctioneerOnsiteEnabled?: boolean
  liveAuction?: Partial<LiveAuction>
}

const detailContent = ({
  isInTour,
  isRemoteBidEnabled,
  listing,
  isRedesignEnabled,
  isAuctioneerOnsiteEnabled = false,
  liveAuction,
}: DetailContentProps) => {
  // const {
  //   listing_configuration: listingConfiguration,
  //   listing_status: listingStatus,
  //   current_user_remote_bid: currentUserRemoteBid,
  // } = listing

  // const {
  //   postponement_new_auction_date: postponedDate,
  //   postponement_new_auction_time: postponedTime,
  // } = listingConfiguration

  const remoteBidLabel: RowDataProps | null =
    isRemoteBidEnabled && listing.current_user_remote_bid?.remote_bid_amount
      ? {
          label: (
            <Text
              data-elm-id='your_remote_bid_label'
              color='success'
              weight='heavy'
              className='u-mb-2'
            >
              <Icon name='check-circle' iconStyle='solid' color='success' />
              Your Remote Bid:{' '}
              {utils.formatters.formatMoney(
                listing.current_user_remote_bid?.remote_bid_amount,
                {}
              )}
            </Text>
          ),
        }
      : null
  const isPostponed = [
    graph.enums.ListingStatus.Postponed,
    graph.enums.ListingStatus.PostponedPendingScheduling,
  ].includes(listing.listing_status)

  const auctionDate = isPostponed
    ? listing.listing_configuration?.postponement_new_auction_date
    : listing.auction?.auction_date // which is better, listing.auction.auction_date or segment.start_date?
  // @ts-ignore needs ts v5
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const trusteeTime = listing?.trustee?.sale_time
  const postponementTime =
    listing.listing_configuration?.postponement_new_auction_time
  const bidStartTime = listing?.venue?.venue_schedule?.bid_start_local_time
  const comingSoon = listing?.is_coming_soon
  // @ts-ignore needs ts v5
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const auctionTime: string = match({
    isPostponed,
    postponementTime,
    bidStartTime,
    trusteeTime,
    comingSoon,
  })
    .with(
      { isPostponed: true, postponementTime: P.not(P.nullish) },
      ({ postponementTime }) => date.format(postponementTime, 'LT', 'LTS')
    )
    .with(
      { bidStartTime: P.nullish, trusteeTime: P.not(P.nullish) },
      ({ trusteeTime }) => date.format(trusteeTime, 'LT', 'LTS')
    )
    .with(
      {
        comingSoon: true,
        trusteeTime: P.not(P.nullish),
      },
      ({ trusteeTime }) => date.format(trusteeTime, 'LT', 'LTS')
    )
    .with({ comingSoon: true, trusteeTime: P.nullish }, () => null)
    .with({ bidStartTime: P.not(P.nullish) }, ({ bidStartTime }) =>
      date.format(bidStartTime, 'LT', 'LTS')
    )
    .otherwise(() => null)

  const displayTime =
    !auctionTime || auctionTime === '12:00 AM'
      ? 'TBD'
      : auctionTime.toLowerCase()
  // This is an odd case where we schedule into essentially dummy venues for coming soon events. There is no way to tell
  // them apart because it's so ad hoc so instead of trying we attempt to confirm the address is good enough to show on
  // the UI.
  const hasRepipeFullAddressParts =
    !_.isEmpty(listing) &&
    Object.values(
      _.pick(listing?.venue?.venue_address, [
        'street_name',
        'municipality',
        'postal_code',
        'country_primary_subdivision',
      ])
    ).every(Boolean)

  // prettier-ignore
  const isShowAddress =
    listing?.listing_configuration?.product_type === graph.enums.ProductType.Trustee &&
    listing?.event?.event_type === graph.enums.EventType.Live &&
    !listing?.listing_configuration?.is_third_party_online &&
    hasRepipeFullAddressParts

  const venueAddressFormatted = formattedVenueAddress(listing)

  const hasAuctionsCompleted =
    liveAuction?.status === graph.enums.LiveAuctionStatus.Completed
  const isLocationUpdated = Boolean(
    liveAuction?.location?.lat && liveAuction?.location?.lon
  )
  const venueLocation = liveAuction?.location
  const isAuctioneerOnsite = isLocationUpdated && !hasAuctionsCompleted
  const mapsQuery = isLocationUpdated
    ? encodeURIComponent(`${venueLocation!.lat},${venueLocation!.lon}`)
    : venueAddressFormatted || listing.venue?.venue_name || ''
  const googleMapsBaseUrl = 'https://www.google.com/maps'
  const mapsSearchUrl = `${googleMapsBaseUrl}/search/?api=1&query=${mapsQuery}`
  const mapsDirectionsUrl = `${googleMapsBaseUrl}/dir/?api=1&destination=${mapsQuery}`

  const location = [
    {
      label: (
        <Text color='gray' data-elm-id='location_label'>
          Location
        </Text>
      ),
      value: (
        <>
          <a
            target='_BLANK'
            rel='noreferrer'
            data-elm-id='venue_address_google_maps_link'
            href={mapsSearchUrl}
          >
            <Text weight='heavy' data-elm-id='venue_name' color='info' as='b'>
              <TruncateText>{listing.venue?.venue_name}</TruncateText>
            </Text>
            {isShowAddress && (
              <Text
                weight='heavy'
                data-elm-id='venue_address'
                color='info'
                as='b'
              >
                <TruncateText className={cx('venue-max-width')}>
                  {venueAddressFormatted}
                </TruncateText>
              </Text>
            )}
          </a>
          {isAuctioneerOnsiteEnabled && isAuctioneerOnsite && (
            <>
              <div className={cx('directions-wrapper')}>
                <Button
                  role='auxiliary'
                  color={Button.ButtonColors.Error}
                  size='small'
                  tag='a'
                  link={mapsDirectionsUrl}
                  target='_blank'
                  rel='noreferrer'
                  elmId='directions_button'
                  className={
                    isLocationUpdated ? cx('directions-updated') : undefined
                  }
                >
                  <Icon
                    name='diamond-turn-right'
                    iconStyle='regular'
                    color={Button.ButtonColors.Error}
                  />{' '}
                  Auctioneer Onsite
                </Button>
                <>
                  <span className={cx('directions-dot')} />
                  <Pulser
                    className={cx('directions-pulser')}
                    width={20}
                    color={constants.ColorNames.Error}
                  />
                </>
              </div>
              <Text.Body
                color='gray'
                size='extraSmall'
                data-elm-id='location_updated_label'
              >
                Exact Auction Location
              </Text.Body>
            </>
          )}
        </>
      ),
      displayBlock: true,
    },
  ]

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const formattedAuctionDate = date.format(auctionDate, DAY_OF_MONTH_FORMAT)
  const withDate = [
    {
      label: (
        <Text color='gray' data-elm-id='date_label'>
          Date
        </Text>
      ),
      value: (
        <Text color='gray' weight='heavy' data-elm-id='date_value'>
          {formattedAuctionDate || 'TBD'}
          {formattedAuctionDate && <CalendarPopup isInTour={isInTour} />}
        </Text>
      ),
    },
    {
      label: (
        <Text color='gray' data-elm-id='auction_start_time_label'>
          Auction Start Time
        </Text>
      ),
      value: (
        <Text
          color='gray'
          weight='heavy'
          data-elm-id='auction_start_time_value'
        >
          {displayTime}
        </Text>
      ),
    },
  ]

  const withOutDate = [
    {
      label: (
        <Text className='u-pb-2'>
          <strong>Postponed: </strong>
          <br />
          Awaiting new auction date and time
        </Text>
      ),
    },
  ]
  const details: RowDataProps[] = isAuctionSold(listing) // was bStatus.isAuctionSold
    ? location
    : [
        remoteBidLabel,
        ...(isPostponed &&
        !listing.listing_configuration?.postponement_new_auction_date
          ? withOutDate
          : withDate),
        ...location,
      ].filter((item): item is RowDataProps => item !== null)
  const ervRow = getERVRow(listing, isRedesignEnabled)
  ervRow && details.unshift(ervRow)

  return details
}

interface PrimaryButtonProps {
  listing: CombinedListingType
  property: Property
  propertyActions: PropertyPageActionsType | undefined
  similarProperties?: any[]
  isThirdPartyOnline?: boolean
  isRemoteBidEnabled?: boolean
  user: blueprint.primitives.AuctionUser
}

const primaryButton = ({
  listing,
  property,
  propertyActions,
  similarProperties,
  isThirdPartyOnline,
  isRemoteBidEnabled,
  user,
}: PrimaryButtonProps) => {
  if (isAuctionSold(listing))
    return (
      hasSimilarProperties(similarProperties) &&
      similarPropertiesLink({
        listing,
        isButton: true,
        hasSimilarProperties: true,
      })
    )

  let venueUrl = listing?.venue?.venue_url || null

  const trusteeReferenceId = listing.trustee_reference_id

  try {
    if (venueUrl) {
      const venueUrlObj = new URL(venueUrl)
      if (
        !venueUrlObj.searchParams.has('rdr') &&
        !_.isNil(trusteeReferenceId)
      ) {
        venueUrlObj.searchParams.append('rdr', trusteeReferenceId)
        venueUrl = venueUrlObj.toString()
      }
    }
  } catch (e) {
    venueUrl = null
  }

  if ((isFCLMarketing(listing) || isThirdPartyOnline) && venueUrl) {
    return (
      <BidAtCountySite venueUrl={venueUrl}>
        <Button size='large' role='primary' elmId='bid_at_county_button'>
          Bid at County Site
        </Button>
      </BidAtCountySite>
    )
  }

  let actualRemoteBiddingButton = null

  if (isRemoteBidEnabled) {
    const isUserLoggedIn = user.logged_in
    const isUserRemoteBidQualified =
      isUserLoggedIn && user.is_current_user_remote_bid_qualified

    const listingId = listing.listing_id

    actualRemoteBiddingButton = (
      <div className={cx('u-mb-2')}>
        <RemoteBidButton
          isUserRemoteBidQualified={isUserRemoteBidQualified}
          listingId={listingId}
        />
      </div>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const isSaved = Boolean(property.is_currently_saved)
  return (
    <>
      {actualRemoteBiddingButton}
      <SavePropertyButton
        listing={listing}
        property={property}
        actions={propertyActions}
        isSaved={isSaved}
        disabled={Boolean(actualRemoteBiddingButton) && isSaved}
        options={{
          text: SAVE_PROPERTY_CTA_TPS,
          size: 'large',
          ...(Boolean(actualRemoteBiddingButton) && {
            elmId: 'secondary_save_property_button',
            role: 'secondary',
          }),
        }}
      />
    </>
  )
}

interface TitleProps {
  listing: CombinedListingType
  isRemoteBidEnabled: boolean
  isThirdPartyOnline?: boolean
}

const title = ({
  listing,
  isRemoteBidEnabled,
  isThirdPartyOnline,
}: TitleProps) => {
  const liveEventText = isRemoteBidEnabled
    ? 'In-Person & Remote Bidding'
    : 'In-Person Auction'
  const type =
    !listing.auction?.is_online && !isThirdPartyOnline
      ? liveEventText
      : 'Online Auction'
  const status = isAuctionSold(listing) // was bStatus.isAuctionSold
    ? ' - Ended'
    : ''
  return type + status
}

interface TPSProps {
  similarProperties?: any[]
  isInTour?: boolean
  pushAuthQueueItem: (item: any) => void
  isRedesignEnabled?: boolean
  user: blueprint.primitives.AuctionUser
  onClickAiPrompt: (prompt?: string) => void
}

const TPS = ({
  similarProperties,
  isInTour,
  isRedesignEnabled,
  user,
  onClickAiPrompt,
}: TPSProps) => {
  const { updatePresenceContext } = useContext(PresenceContext)
  const { listing } = useListingContext()
  const { property, propertyActions } = usePropertyContext()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { graphWebsocket, featureFlags } = useApplicationContext()

  const isTrusteeLive = listing?.__methods__?.isTPSLive?.()
  const hasLiveAuction = Boolean(listing?.live_auction_segment?.live_auction)
  const venueId = listing?.venue?.venue_id

  const { data: liveAuctionLocationData } =
    blueprint.bidding.hooks.useLiveAuctionLocation({
      enabled: Boolean(isTrusteeLive && hasLiveAuction && venueId),
      graphWebsocket,
      initialData: {
        liveAuctions: hasLiveAuction
          ? [
              {
                ...listing?.live_auction_segment?.live_auction,
                venue_id: venueId,
              },
            ]
          : [],
      },
    })
  const liveAuction: Partial<LiveAuction> | undefined = venueId
    ? liveAuctionLocationData.mapped?.get(venueId)
    : undefined

  const isThirdPartyOnline =
    listing?.listing_configuration?.is_third_party_online

  const isShowSecondaryButton =
    !isFCLMarketing(listing) && !isAuctionSold(listing) && !isThirdPartyOnline

  // ^ was:
  // !bStatus.isFCLMarketing &&
  // !bStatus.isAuctionSold &&
  // !isThirdPartyOnline

  const hasEnabledRemoteBidFlag = listing.__methods__.isRemoteBidEnabled()

  const isRemoteBidEnabled =
    featureFlags['fe.resi.enable-web-remote-bidding'] && hasEnabledRemoteBidFlag
  const isAuctioneerOnsiteEnabled =
    featureFlags['fe.resi.is_auctioneer_onsite_direction']

  const aiFaqMode = getAiFaqMode(featureFlags)

  // TODO: This whole thing is so darn convuluted. We have primary/secondary but there's about a zillion conditions and
  // then we layered on an experiment there also.
  //
  // Also something we call a button is mostly a link.
  //
  // Also note - currently tying the enablement to if the FAQ's are enabled with AI as these are rolled out together.
  const secondaryButton =
    (isRemoteBidEnabled &&
      remoteBidEducationLink(Boolean(aiFaqMode), onClickAiPrompt)) ||
    whySaveLink()

  return (
    <div
      onMouseEnter={() => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        updatePresenceContext((ctx: any) => ({ ...ctx, hovering_on_bid: true }))
      }}
      onMouseLeave={() => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        updatePresenceContext((ctx: any) => ({
          ...ctx,
          hovering_on_bid: false,
        }))
      }}
    >
      <AuctionDetailsTemplate
        isTPS
        title={title({
          listing,
          isRemoteBidEnabled: Boolean(isRemoteBidEnabled),
          isThirdPartyOnline: Boolean(isThirdPartyOnline),
        })}
        primaryButton={primaryButton({
          property,
          propertyActions,
          similarProperties,
          isThirdPartyOnline: Boolean(isThirdPartyOnline),
          isRemoteBidEnabled: Boolean(isRemoteBidEnabled),
          listing,
          user,
        })}
        secondaryButton={isShowSecondaryButton && secondaryButton}
        detailContent={detailContent({
          isInTour,
          listing,
          isRemoteBidEnabled: Boolean(isRemoteBidEnabled),
          isRedesignEnabled,
          isAuctioneerOnsiteEnabled: Boolean(isAuctioneerOnsiteEnabled),
          liveAuction,
        })}
        additionalContent={tpsAdditionalContent(
          false,
          !isAuctionSold(listing) // was !bStatus.isAuctionSold
        )}
      />
    </div>
  )
}

// TPS.propTypes = AuctionDetailsPropTypes

export default TPS
